/**
 * EU VAT number formats and country-prefix handling.
 *
 * Chargebee's EU VAT / Brexit handling auto-prepends the billing address's
 * country code onto `vat_number` when rendering it (e.g. "VAT Reg Number
 * #FI35515055" for country "FI") — documented only for the EU VAT area plus
 * GB and XI (Northern Ireland), NOT for other countries (US EIN, AU ABN,
 * etc.), where `vat_number` is stored/displayed verbatim. Greece uses VAT
 * prefix "EL" rather than its ISO country code "GR".
 *
 * Single source of truth shared by the API (server-side strip/validate
 * before calling Chargebee) and the web app (live input formatting/
 * validation) so the two cannot drift.
 */

/** Per-country VAT number body format (the part AFTER the 2-letter prefix), per the VIES reference table. */
export const VAT_FORMATS: Record<string, RegExp> = {
	AT: /^U\d{8}$/,
	BE: /^[01]\d{9}$/,
	BG: /^\d{9,10}$/,
	CY: /^\d{8}[A-Z]$/,
	CZ: /^\d{8,10}$/,
	DE: /^\d{9}$/,
	DK: /^\d{8}$/,
	EE: /^\d{9}$/,
	EL: /^\d{9}$/,
	ES: /^[A-Z0-9]\d{7}[A-Z0-9]$/,
	FI: /^\d{8}$/,
	FR: /^[A-Z0-9]{2}\d{9}$/,
	GB: /^(\d{9}|\d{12}|(GD|HA)\d{3})$/,
	HR: /^\d{11}$/,
	HU: /^\d{8}$/,
	IE: /^(\d{7}[A-Z]{1,2}|\d[A-Z+*]\d{5}[A-Z])$/,
	IT: /^\d{11}$/,
	LT: /^(\d{9}|\d{12})$/,
	LU: /^\d{8}$/,
	LV: /^\d{11}$/,
	MT: /^\d{8}$/,
	NL: /^\d{9}B\d{2}$/,
	PL: /^\d{10}$/,
	PT: /^\d{9}$/,
	RO: /^\d{2,10}$/,
	SE: /^\d{12}$/,
	SI: /^\d{8}$/,
	SK: /^\d{10}$/,
	XI: /^(\d{9}|\d{12}|(GD|HA)\d{3})$/,
};

/**
 * Countries where Chargebee's EU VAT / Brexit handling auto-prepends the
 * billing country code onto `vat_number`. Derived from `VAT_FORMATS`.
 */
export const EU_VAT_PREFIX_COUNTRIES = new Set(Object.keys(VAT_FORMATS));

/**
 * The VAT prefix Chargebee associates with a given ISO billing country, or
 * `undefined` if the country is outside the EU VAT area (Chargebee doesn't
 * auto-prepend a country code for those, e.g. US, AU).
 */
export const vatPrefixCountryCode = (
	country: string | undefined | null,
): string | undefined => {
	if (!country) {
		return undefined;
	}
	const upper = country.toUpperCase();
	if (upper === "GR") {
		return "EL";
	}
	return EU_VAT_PREFIX_COUNTRIES.has(upper) ? upper : undefined;
};

const PREFIXED_CODE_RE = /^([A-Za-z]{2})(.+)$/;

/**
 * Strips whitespace and, if the resulting value starts with a 2-letter
 * prefix matching the given country's VAT prefix, strips that prefix unless
 * the whole value is already a valid body (e.g. a French body beginning FR).
 * Outside the EU VAT area, or when the prefix doesn't match, the value is
 * only whitespace-trimmed and uppercased.
 */
export const sanitizeVatCode = (
	rawInput: string,
	billingCountry: string | undefined | null,
): string => {
	const trimmed = rawInput.replace(/\s+/g, "").toUpperCase();
	const expectedPrefix = vatPrefixCountryCode(billingCountry);
	if (!expectedPrefix) {
		return trimmed;
	}
	const format = VAT_FORMATS[expectedPrefix];
	if (format?.test(trimmed)) {
		return trimmed;
	}
	const match = trimmed.match(PREFIXED_CODE_RE);
	const prefix = match?.[1];
	const rest = match?.[2];
	if (prefix && rest && prefix === expectedPrefix) {
		return rest;
	}
	return trimmed;
};

/**
 * Validates a VAT code body (without country prefix) against the billing
 * country's known VIES format. Returns `true` for countries outside the EU
 * VAT area (format unknown/not applicable — Chargebee performs its own
 * validation for those) or when the code is empty.
 */
export const isValidVatCodeFormat = (
	code: string,
	billingCountry: string | undefined | null,
): boolean => {
	if (!code) {
		return true;
	}
	const expectedPrefix = vatPrefixCountryCode(billingCountry);
	if (!expectedPrefix) {
		return true;
	}
	const format = VAT_FORMATS[expectedPrefix];
	return format ? format.test(code) : true;
};

/**
 * Throws-style check: whether a full VAT number's leading 2-letter prefix
 * (if present) matches the expected prefix for the billing country. Returns
 * `true` when there's no prefix to check, or the country is outside the EU
 * VAT area.
 */
export const hasMatchingVatCountryPrefix = (
	vatNumber: string,
	billingCountry: string | undefined | null,
): boolean => {
	const expectedPrefix = vatPrefixCountryCode(billingCountry);
	if (!expectedPrefix) {
		return true;
	}
	if (isValidVatCodeFormat(vatNumber, billingCountry)) {
		return true;
	}
	const match = vatNumber.match(PREFIXED_CODE_RE);
	const prefix = match?.[1];
	if (!prefix) {
		return true;
	}
	return prefix.toUpperCase() === expectedPrefix;
};

/**
 * Strips a leading 2-letter country-code prefix from a VAT number when the
 * whole value isn't already a valid body for that country. Call only after
 * `hasMatchingVatCountryPrefix` has confirmed the prefix (if any) matches.
 */
export const stripVatCountryPrefix = (
	vatNumber: string,
	billingCountry: string | undefined | null,
): string => {
	if (!vatPrefixCountryCode(billingCountry)) {
		return vatNumber;
	}
	if (isValidVatCodeFormat(vatNumber, billingCountry)) {
		return vatNumber;
	}
	const match = vatNumber.match(PREFIXED_CODE_RE);
	return match?.[2] ?? vatNumber;
};
